import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Leaf, ChevronRight, Download, UserCircle2 } from "lucide-react";
import PageHero from "../components/ui-custom/PageHero";
import GlassCard from "../components/ui-custom/GlassCard";
import Reveal from "../components/ui-custom/Reveal";
import StatCounter from "../components/ui-custom/StatCounter";
import EsgSections from "../components/ui-custom/EsgSections";
import { formatDateTime } from "../lib/formatDate";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const ReportDownloadCard = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(""); // "" | "emailed" | "downloaded"

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    axios
      .post(`${API}/sustainability/report-request`, { email: email.trim() })
      .then((res) => {
        const link = document.createElement("a");
        link.href = res.data.download_url;
        link.download = "QP-Group-Sustainability-Report-FY2025-26.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
        setDone(res.data.emailed ? "emailed" : "downloaded");
        if (!res.data.emailed && res.data.email_error) {
          setError(`Email didn't send: ${res.data.email_error}`);
        }
      })
      .catch((err) => setError(err.response?.data?.detail || "Could not process that request. Please try again."))
      .finally(() => setSubmitting(false));
  };

  return (
    <GlassCard accent="amber" className="p-7 flex flex-wrap items-center justify-between gap-5">
      <div>
        <p className="section-eyebrow mb-2">Group Report</p>
        <h3 className="font-display font-semibold text-foreground text-lg mb-1">
          Download the QP Group Sustainability Report
        </h3>
        <p className="text-sm text-muted-foreground max-w-xl">
          FY 2025-26 energy and emissions summary across all sites, plus the current ESG roadmap and gaps.
        </p>
      </div>
      {done ? (
        <p className="text-sm text-qcyan font-medium">
          {done === "emailed"
            ? "Thanks — we've emailed you the report, and your download has started."
            : "Thanks — your download has started."}
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-2">
          <Input
            type="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="min-w-[220px]"
          />
          <Button type="submit" className="btn-glow-primary gap-2 flex-none" disabled={submitting}>
            <Download size={16} /> {submitting ? "Please wait…" : "Get the Report"}
          </Button>
        </form>
      )}
      {error && <p className="text-sm text-destructive w-full">{error}</p>}
    </GlassCard>
  );
};

const RACI_LABELS = { R: "Responsible", A: "Accountable", C: "Consulted", I: "Informed" };
const RACI_ORDER = { R: 0, A: 1, C: 2, I: 3 };

const RaciRibbon = ({ raci }) => {
  if (!raci || raci.length === 0) return null;
  const sorted = [...raci].sort((a, b) => (RACI_ORDER[a.raci] ?? 9) - (RACI_ORDER[b.raci] ?? 9));
  return (
    <div className="flex flex-wrap items-center gap-3 px-6 pb-5 -mt-1 border-t border-border/10 pt-4">
      {sorted.map((p) => (
        <div key={p.email} className="flex items-center gap-2" title={`${p.name} — ${RACI_LABELS[p.raci] || p.raci}`}>
          {p.photo_data_url ? (
            <img
              src={p.photo_data_url}
              alt={p.name}
              className="w-8 h-8 rounded-full object-cover border border-border/20 flex-none"
            />
          ) : (
            <span className="w-8 h-8 rounded-full flex items-center justify-center bg-qcyan/10 border border-qcyan/25 flex-none">
              <UserCircle2 size={16} className="text-qcyan" />
            </span>
          )}
          <div className="leading-tight">
            <p className="text-xs text-foreground font-medium">{p.name}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {p.raci} · {RACI_LABELS[p.raci] || p.raci}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

const SiteCard = ({ site }) => (
  <Reveal>
    <GlassCard className="overflow-hidden hover:border-qcyan/30 transition-colors">
      <Link to={`/sustainability/site/${site.site_id}`}>
        <div className="w-full flex items-center justify-between gap-4 p-6 text-left">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-qcyan/10 border border-qcyan/25 flex-none">
              <Leaf size={20} className="text-qcyan" />
            </span>
            <div>
              <h3 className="font-display font-semibold text-foreground text-lg">{site.name}</h3>
              <p className="text-xs text-muted-foreground">Sustainability &amp; ESG tracking</p>
            </div>
          </div>
          <span className="flex items-center gap-1 text-xs font-semibold text-qblue uppercase tracking-wide flex-none">
            View data <ChevronRight size={14} />
          </span>
        </div>
      </Link>
      <RaciRibbon raci={site.raci} />
    </GlassCard>
  </Reveal>
);

const GroupSummaryDashboard = () => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/sustainability/summary`)
      .then((res) => setSummary(res.data))
      .catch(() => setSummary(null));
  }, []);

  if (!summary || summary.sites_with_data === 0) return null;

  return (
    <Reveal>
      <GlassCard className="p-7">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <p className="section-eyebrow">Group Snapshot</p>
          {summary.fetched_at && (
            <p className="text-xs text-muted-foreground">Data as of {formatDateTime(summary.fetched_at)}</p>
          )}
        </div>
        <h3 className="font-display font-semibold text-foreground text-lg mb-1">
          FY 2025-26 ESG Performance Across the Group
        </h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
          Totals rolled up from {summary.sites_with_data} of {summary.site_count} sites currently reporting data.
          Individual site breakdowns follow below.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          <StatCounter value={summary.electricity_kvah} suffix=" kVAh" label="Electricity Consumed" glow="cyan" />
          <StatCounter value={summary.solar_kwh} suffix=" kWh" label="Solar Generated" glow="amber" />
          <StatCounter value={summary.ghg_total_tco2e} suffix=" t" label="GHG Emissions (CO2e)" glow="cyan" />
          <StatCounter value={summary.water_withdrawal_kl} suffix=" kL" label="Water Withdrawn" glow="cyan" />
          <StatCounter value={summary.waste_kg} suffix=" kg" label="Waste Generated" glow="amber" />
          <StatCounter value={summary.diesel_litres} suffix=" L" label="Diesel Consumed" glow="cyan" />
          <StatCounter value={summary.co2_gas_kg} suffix=" kg" label="CO2 Gas Consumed" glow="amber" />
          <StatCounter value={summary.hvac_gas_kg} suffix=" kg" label="HVAC Gas Consumed" glow="cyan" />
          <StatCounter value={summary.lpg_kg} suffix=" kg" label="LPG Consumed" glow="amber" />
          <StatCounter value={summary.compressed_air_nm3} suffix=" Nm3" label="Compressed Air" glow="cyan" />
          <StatCounter value={summary.paints_kg} suffix=" kg" label="Paints & Consumables" glow="amber" />
          <StatCounter value={summary.site_count} label="Sites Tracked" glow="cyan" />
        </div>

        <div className="mt-8 pt-6 border-t border-border/10">
          <EsgSections monthly={summary.monthly} social={summary.social} governance={summary.governance} />
        </div>
      </GlassCard>
    </Reveal>
  );
};

const Sustainability = () => {
  const [sites, setSites] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/sustainability/sites`)
      .then((res) => setSites(res.data.items || []))
      .catch(() => setError("Could not load the site list right now."));
  }, []);

  return (
    <div>
      <PageHero
        eyebrow="Sustainability"
        title="Sustainability &amp; ESG"
        subtitle="Environmental and energy performance tracking across Quality Power's sites, updated directly from each site's own records."
      />

      <section className="px-6 pb-28">
        <div className="max-w-4xl mx-auto space-y-5">
          <Reveal>
            <ReportDownloadCard />
          </Reveal>

          <GroupSummaryDashboard />

          {error && (
            <GlassCard className="p-8 text-center">
              <p className="text-foreground font-medium">{error}</p>
            </GlassCard>
          )}

          {sites === null && !error && <p className="text-sm text-muted-foreground text-center">Loading sites…</p>}

          {sites && sites.length === 0 && (
            <GlassCard className="p-10 text-center">
              <p className="text-foreground font-medium">No sites have been added yet.</p>
              <p className="text-sm text-muted-foreground mt-1">Check back soon for site-level sustainability data.</p>
            </GlassCard>
          )}

          {sites && sites.length > 0 && (
            <p className="section-eyebrow pt-4">By Site</p>
          )}

          {sites?.map((site) => (
            <SiteCard key={site.site_id} site={site} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default Sustainability;
